<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type {
  ContentDistributionGroupMembership,
  InstanceAssignmentGroupMembership,
  InstanceMembership,
  Collaboration as CollaborationDTO,
} from '@openapi'
import type { XOR } from '@lib/util/tsHelpers'
import CCard from '@lib/components/CCard/CCard.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { computed } from 'vue'

type Collaboration = Pick<CollaborationDTO, 'id' | 'file' | 'role' | 'user' | 'content_distribution_group' | 'folder'>
type Item = XOR<
  [Collaboration, ContentDistributionGroupMembership, InstanceMembership, InstanceAssignmentGroupMembership]
>

interface Props {
  items: Item[]
  maxItemsToShow?: number
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  maxItemsToShow: 5,
})

const { t } = useI18n()
const themeVars = useThemeVars()

const roleItemsWithGroup = computed(() => {
  return props.items.slice(0, props.maxItemsToShow).map((item) => {
    if ('content_distribution_group' in item) {
      const { content_distribution_group, ...rest } = item
      return { ...rest, group: content_distribution_group }
    } else if ('instance_assignment_group' in item) {
      const { instance_assignment_group, ...rest } = item
      return { ...rest, group: instance_assignment_group }
    } else {
      throw new Error(
        "CGroupsAccessInfo: Invalid item, doesn't contain 'content_distribution_group' or 'instance_assignment_group'",
      )
    }
  })
})
</script>

<template>
  <CCard class="pa-2 c-groups-access-info">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-4">
        <CIcon icon="users" />
        <div class="font-bold">
          {{ title ?? t('canvasUI.components.CGroupsAccessInfo.title', items.length) }}
        </div>
      </div>

      <div class="flex flex-wrap">
        <div v-for="({ group }, index) in roleItemsWithGroup" :key="group.id">
          <span>{{ group.name }}</span>
          <span v-if="index < roleItemsWithGroup.length - 1">,&nbsp;</span>
        </div>
        <div v-if="items.length > maxItemsToShow" class="ml-2 truncate c-groups-access-info__more">
          {{ t('canvasUI.components.CGroupsAccessInfo.more', items.length - maxItemsToShow) }}
        </div>
      </div>
    </div>

    <slot />
  </CCard>
</template>

<style lang="scss" scoped>
.c-groups-access-info {
  &__more {
    color: v-bind('themeVars.text2');
  }
}
</style>
